import { AlertCircle, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { Checkbox } from "../../../components/checkbox";
import { CustomModal } from "../../../components/modal";
import { Label } from "../../../components/label";

interface NotificationModalsProps {
  deleteDialogOpen: boolean;
  notificationTitle?: string;
  deleting: boolean;
  deleteError?: string | null;
  onDeleteCancel: () => void;
  onDeleteConfirm: () => void;
  pinDialogOpen: boolean;
  isPinning: boolean;
  pinError?: string | null;
  dontShowAgain: boolean;
  onDontShowAgainChange: (checked: boolean) => void;
  onPinCancel: () => void;
  onPinConfirm: () => void;
}

export function NotificationModals({
  deleteDialogOpen,
  notificationTitle,
  deleting,
  deleteError,
  onDeleteCancel,
  onDeleteConfirm,
  pinDialogOpen,
  isPinning,
  pinError,
  dontShowAgain,
  onDontShowAgainChange,
  onPinCancel,
  onPinConfirm,
}: NotificationModalsProps) {
  const { t } = useTranslation();

  return (
    <>
      <CustomModal
        isOpen={deleteDialogOpen}
        onClose={onDeleteCancel}
        title={t("notification.delete_confirm_title")}
        description={t("notification.delete_confirm_description", {
          title: notificationTitle,
        })}
        showCloseButton={!deleting}
      >
        {deleteError && (
          <div
            className="mb-3 flex items-center gap-2 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
            role="alert"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {deleteError}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onDeleteCancel}
            disabled={deleting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={onDeleteConfirm}
            disabled={deleting}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
          >
            {deleting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                {t("notification.deleting")}
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                {t("common.delete")}
              </>
            )}
          </Button>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={pinDialogOpen}
        onClose={onPinCancel}
        title={t("notification.pin_confirm_title")}
        description={t("notification.pin_confirm_description")}
        showCloseButton={!isPinning}
      >
        {pinError && (
          <div
            className="mb-3 flex items-center gap-2 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
            role="alert"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {pinError}
          </div>
        )}
        <div className="flex items-center space-x-2 py-4">
          <Checkbox
            id="dont-show-again"
            checked={dontShowAgain}
            onChange={(event) => onDontShowAgainChange(event.target.checked)}
          />
          <Label
            htmlFor="dont-show-again"
            className="text-sm"
          >
            {t("notification.dont_show_again")}
          </Label>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onPinCancel}
            disabled={isPinning}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={onPinConfirm}
            disabled={isPinning}
          >
            {isPinning ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                {t("notification.pinning")}
              </div>
            ) : (
              t("notification.pin_notification")
            )}
          </Button>
        </div>
      </CustomModal>
    </>
  );
}
